import * as yup from 'yup'
import { currencyRegex } from '../pages/protected/loanCalculator/utils/helpers'
import { applyMask } from './utils'
import { currencyToFloat } from './formattingUtils'
import Decimal from 'decimal.js'

const yupOptionalNumberTransform = (value) =>
	Number.isNaN(value) ? null : value

/**
 * @deprecated use yup.mixed().optionalIntegerRange method instead
 */
const yupOptionalIntegerRange = (min, max) => {
	return yup
		.number()
		.transform(yupOptionalNumberTransform)
		.nullable()
		.optional()
		.integer()
		.min(min)
		.max(max)
}

const addCustomYupValidators = () => {
	yup.addMethod(yup.mixed, 'nullableNumber', function () {
		return yup
			.number()
			.transform((v, o) => (o === '' ? null : v))
			.nullable()
	})

	yup.addMethod(yup.mixed, 'guid', function () {
		return yup
			.string()
			.matches(
				/^{?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}}?$/,
				'Please provide a valid GUID'
			)
	})

	yup.addMethod(
		yup.mixed,
		'optionalIntegerRange',
		function (min, max) {
			return yup
				.number()
				.transform(yupOptionalNumberTransform)
				.nullable()
				.optional()
				.integer()
				.min(min)
				.max(max)
		}
	)
}

export const yupPercentageValidator = (fieldName = 'Percentage') => {
	return yup
		.string()
		.test(
			'is-valid-percent',
			(d) => `${d.path} must be between 0-100`,
			(value) => {
				if (!value) {
					return true
				}
				return parseFloat(value) >= 0 && parseFloat(value) <= 100
			}
		)
		.matches(/^\d*\.?\d+$/, {
			message: `${fieldName} must be a valid number.`,
			excludeEmptyString: true,
		})
}

export const yupZipCodeValidator = (fieldName = 'ZIP Code') => {
	return yup
		.string()
		.test(
			'is-valid-zip-code',
			'ZIP Code must be a valid 5 digit number.',
			(value) => {
				if (!value) {
					return true
				}
				return value.length === 5
			}
		)
		.matches(/^[0-9]+$/, {
			message: `${fieldName} must be a valid 5 digit number.`,
			excludeEmptyString: true,
		})
}

export const yupCreditScoreValidator = (
	fieldName = 'Credit Score'
) => {
	return yup
		.string()
		.test(
			'is-valid-credit-score',
			'Credit Score must be between 300-850',
			(value) => {
				if (!value) {
					return true
				}
				return parseFloat(value) >= 300 && parseFloat(value) <= 850
			}
		)
		.matches(/^[0-9]+$/, {
			message: `${fieldName} must be a number.`,
			excludeEmptyString: true,
		})
}

export const yupCurrencyValidator = (fieldName = 'Currency') => {
	return yup.string().matches(currencyRegex, {
		message: `${fieldName} must be a valid dollar amount.`,
		excludeEmptyString: true,
	})
}

export const yupRateValidator = (fieldName = 'Rate') => {
	return yup.string().matches(/^[0-9]+(\.[0-9]+)?$/, {
		message: 'Rate must be a valid number.',
		excludeEmptyString: true,
	})
}

const getCurrencyRangeErrorMessage = (min, max) => {
	return `Must be between ${applyMask(min, 'currency')} and ${applyMask(
		max,
		'currency'
	)}`
}

export const yupHomeValueValidator = () => {
	const min = 25000
	const max = 10_000_000
	return yupCurrencyRangeValidator(min, max)
}

export function yupNumberValidator() {
	return yup
		.number()
		.transform((number) =>
			Number.isNaN(number) ? undefined : number
		)
}

export const yupLoanAmountValidator = yupHomeValueValidator

const isEmptyOrNumber = (value, originalValue) => {
	return originalValue === '' || originalValue === null
		? undefined
		: value
}

export const yupInterestRateValidator = () => {
	const min = 1
	const max = 25
	return yup.number().transform(isEmptyOrNumber).min(min).max(max)
}

export const yupPmiValidator = () => {
	const min = 0
	const max = 10
	return yup.number().transform(isEmptyOrNumber).min(min).max(max)
}

export const yupPointsValidator = () => {
	const min = 0
	const max = 3
	return yup.number().transform(isEmptyOrNumber).min(min).max(max)
}

export const yupOriginationFeePercentageValidator = () => {
	const min = 0
	const max = 5
	return yup.number().transform(isEmptyOrNumber).min(min).max(max)
}

export const yupClosingCostsValidator = () => {
	const min = 500
	const max = 100000
	return yupCurrencyRangeValidator(min, max)
}

export const yupNumberRangeValidator = (min, max) => {
	return yup.number().transform(isEmptyOrNumber).min(min).max(max)
}

export const yupCurrencyRangeValidator = (min, max) => {
	const errorMessage = getCurrencyRangeErrorMessage(min, max)
	return yup
		.string()
		.test('is-valid-currency-range', errorMessage, (value) => {
			if (!value) {
				return true
			}

			const minDecimal = new Decimal(min)
			const maxDecimal = new Decimal(max)
			const decimalValue = currencyToFloat(value)
			return (
				decimalValue.gte(minDecimal) && decimalValue.lte(maxDecimal)
			)
		})
}

export { addCustomYupValidators, yupOptionalIntegerRange }
